/**
 * The **spent-room set** — room ids this player can no longer enter (lobby spec
 * §6.7, ruled 2026-07-29 in §20.1).
 *
 * ── What it is for ─────────────────────────────────────────────────────────
 *
 * A player joins a paid tile, leaves before the game starts (100% refund, room
 * spec §6.5) and taps the same tile again. The server refuses with **409
 * `seat_closed`**, and it is right to: `entry:{roomId}:{playerId}` is one key
 * per player per room, so a second join would replay the first debit into a free
 * seat, and LOCKED rule 27's single `refund:{roomId}:{playerId}` means a genuine
 * second charge could never be given back. **The entry key is not moving** —
 * Dex refused it (§16f A38's closing note) and this design does not need it to.
 *
 * The fix is a tile *state*, not a refusal: a refusal costs a tap, arrives after
 * a round trip and repeats; a state is true before the player reaches for the
 * tile. This module is the fact the state is computed from.
 *
 * ── LOCKED — it is not a source of truth and may never gate money ───────────
 *
 * It only ever suppresses an affordance the **server would refuse anyway**. The
 * server stays the authority and §12.4's 409 path is what survives an empty set
 * — a reload, a second device, a cleared session. Nothing here is consulted
 * before a debit, a refund or a seat.
 *
 * ── Ids go in, and only ever come out by being forgotten ───────────────────
 *
 * Three entry points (§6.7's table), all of them **after** the fact:
 *
 *  1. `POST /rooms/:id/leave` returned 200 — the room screen's pre-game Leave.
 *  2. The room screen reached a state in which this player's seat has ended —
 *     T1, T2, T3, T4, T7.
 *  3. A join was refused 409 `seat_closed` — the reload / second-device path.
 *
 * A false entry is unrepresentable in practice: room ids are never reused, and
 * an id only enters after that player's seat in that room has ended. If one ever
 * occurred its cost is one tile that says "you left this one" until the room
 * turns over.
 *
 * ── Session-scoped and keyed by `players.id` ───────────────────────────────
 *
 * In memory, mirrored to `sessionStorage` so a reload of the lobby keeps it;
 * cleared on log out and whenever the identity changes. The key carries the
 * player id, so a second account in the same tab starts empty rather than
 * inheriting someone else's history — which would be the one way a false entry
 * could be manufactured.
 *
 * ── The id space is `rooms.id` ─────────────────────────────────────────────
 *
 * The same space `tiles[].roomId`, the leave response, the room state and the
 * 409 body all speak. §16 note 3b: one space, or the client's comparisons are
 * noise.
 */

const STORAGE_PREFIX = "wr.spentRooms.v1:";

function storageKey(playerId: string): string {
  return `${STORAGE_PREFIX}${playerId}`;
}

/**
 * `sessionStorage`, if this browser has one and lets us use it.
 *
 * Private modes and locked-down embedders throw on access rather than returning
 * `null`, and a lobby that cannot render because storage is disabled would be a
 * worse failure than a `left` state that does not survive a reload. Every path
 * below degrades to memory-only.
 */
function store(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function read(playerId: string): string[] {
  const storage = store();
  if (!storage) return [];
  try {
    const raw = storage.getItem(storageKey(playerId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function write(playerId: string, ids: readonly string[]): void {
  const storage = store();
  if (!storage) return;
  try {
    storage.setItem(storageKey(playerId), JSON.stringify(ids));
  } catch {
    // Quota, or a storage that accepts reads and refuses writes. The in-memory
    // set still works for this screen; only the reload survival is lost.
  }
}

/**
 * The in-memory set, keyed by player id.
 *
 * Module-level rather than React state because three unrelated surfaces write
 * to it (the room screen's leave, the room screen's terminals, the lobby's 409)
 * and the lobby has to see all three without any of them being its child.
 * Subscribers are notified so a mounted lobby re-renders the tile in place —
 * §10.1 permits a state change in place and forbids the geometry moving.
 */
const sets = new Map<string, ReadonlySet<string>>();
const listeners = new Set<() => void>();

/**
 * The stored set is **replaced, never mutated**, and that is load-bearing rather
 * than stylistic: `useSpentRooms` reads it through `useSyncExternalStore`, which
 * compares snapshots with `Object.is` and would skip the re-render entirely if
 * an in-place `.add()` kept the same reference. A tile that stayed joinable
 * after its own 409 is the exact tap §6.7 exists to stop.
 */
function setFor(playerId: string): ReadonlySet<string> {
  const existing = sets.get(playerId);
  if (existing) return existing;
  const restored: ReadonlySet<string> = new Set(read(playerId));
  sets.set(playerId, restored);
  return restored;
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** The ids this player may no longer enter. Never mutated by the caller. */
export function spentRoomIds(playerId: string): ReadonlySet<string> {
  return setFor(playerId);
}

/**
 * Record a room this player's seat has ended in.
 *
 * Idempotent, and a no-op on a blank id — a caller that has no room id (a
 * terminal reached before the state ever named one) must not be forced to
 * branch, and adding `""` would make the membership test match every tile whose
 * `roomId` is absent.
 */
export function recordSpentRoom(playerId: string, roomId: string | null | undefined): void {
  if (!playerId || !roomId) return;
  const set = setFor(playerId);
  if (set.has(roomId)) return;
  const next: ReadonlySet<string> = new Set([...set, roomId]);
  sets.set(playerId, next);
  write(playerId, [...next]);
  notify();
}

/**
 * Forget everything for this player — log out, or an identity change.
 *
 * §6.7: the set is identity-keyed, so a conversion (which changes `players.id`
 * for nobody, but changes *who is signed in* for everybody watching) must not
 * carry one player's spent rooms into another's lobby.
 */
export function clearSpentRooms(playerId?: string): void {
  const storage = store();
  if (playerId) {
    sets.delete(playerId);
    try {
      storage?.removeItem(storageKey(playerId));
    } catch {
      /* see `write` */
    }
    notify();
    return;
  }

  sets.clear();
  try {
    if (storage) {
      const keys: string[] = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
      }
      for (const key of keys) storage.removeItem(key);
    }
  } catch {
    /* see `write` */
  }
  notify();
}

/** Subscribe to changes. Returns the unsubscribe. */
export function subscribeSpentRooms(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
